import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import {
  getBudgetLines,
  getBudgetRollup,
  createBudgetLine,
  updateBudgetLine,
  updateBudgetLinePaymentStatus,
  deleteBudgetLine,
  updateProject,
  getProjectById,
} from "../api/projectApi";
import { formatInr } from "../utils/formatInr";
import { getUsdInrRate } from "../api/budgetApi";
import {
  SPEND_PAYMENT_STATUSES,
  SPEND_PAYMENT_STATUS_LABELS,
  SPEND_PAYMENT_STATUS_COLORS,
  resolveSpendDisplayStatus,
} from "../constants/budgetSpendStatus";
import { AuthContext } from "../auth/AuthContext";
import { BUDGET_ORGANIZATIONS } from "../constants/organizations";
import { getStoredBudgetOrganizationView } from "./BudgetOrganizationSwitcher";

const PERIOD_FILTERS = [
  { value: "last_and_current", label: "Last & current month" },
  { value: "this_month", label: "This month only" },
  { value: "pick_month", label: "Choose month" },
  { value: "year_overview", label: "Year overview" },
];

const currentYear = () => new Date().getFullYear();

const denseCell = { py: 0.5, px: 1, fontSize: "0.8125rem" };

/** Outlined label on border (not floating inside) — matches Month / Payment due fields. */
const outlinedAmountFieldProps = {
  size: "small",
  variant: "outlined",
  InputLabelProps: { shrink: true },
};

const isSpendRow = (row) => row.year_month != null && row.year_month !== "";

/** @param {string|Date} ym */
const ymToYmd = (ym) => {
  if (!ym) return "";
  const s = typeof ym === "string" ? ym.slice(0, 10) : dayjs(ym).format("YYYY-MM-DD");
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const sameMonth = (ymVal, y, month0) => {
  if (!isSpendRow({ year_month: ymVal })) return false;
  const d = dayjs(ymToYmd(ymVal));
  return d.isValid() && d.year() === y && d.month() === month0;
};

const monthTitle = (y, month0) => dayjs(new Date(y, month0, 1)).format("MMMM YYYY");

const spendMatchesOrg = (row, orgView) => {
  if (!orgView) return true;
  return String(row.organization || "").toUpperCase() === String(orgView).toUpperCase();
};

const spendsInMonth = (lines, y, m0, orgView = "") =>
  lines.filter((r) => isSpendRow(r) && sameMonth(r.year_month, y, m0) && spendMatchesOrg(r, orgView));

const sumAmount = (rows) => rows.reduce((s, r) => s + (Number(r.amount_inr) || 0), 0);

function canModifySpendRow(row, { userId, role, canEdit }) {
  if (!canEdit || !isSpendRow(row)) return false;
  if (String(role || "").toLowerCase() === "superadmin") return true;
  const creator = row.created_by != null ? Number(row.created_by) : null;
  if (creator == null) return false;
  return creator === Number(userId);
}

const ymdFor = (y, month0) => dayjs(new Date(y, month0, 1)).format("YYYY-MM-DD");

/** Normalize API date / string for `<input type="date">` */
const paymentDueInputValue = (val) => {
  if (!val) return "";
  const s = typeof val === "string" ? val.slice(0, 10) : dayjs(val).format("YYYY-MM-DD");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  return dayjs(s).isValid() ? s : "";
};

const formatDueDisplay = (val) => {
  const s = paymentDueInputValue(val);
  return s ? dayjs(s).format("DD MMM YYYY") : "—";
};

function SpendAmountUsdHelper({ amountUsd, rateDate, onConverted, onFxMeta }) {
  const [fx, setFx] = useState({ rate: null, rateDate: null, loading: false, error: null });

  useEffect(() => {
    const usd = Number(amountUsd);
    if (!amountUsd || Number.isNaN(usd) || usd <= 0 || !rateDate) {
      setFx({ rate: null, rateDate: null, loading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setFx((f) => ({ ...f, loading: true, error: null }));
    getUsdInrRate(rateDate)
      .then((res) => {
        if (cancelled) return;
        const rate = Number(res.data?.rate);
        const rd = res.data?.rateDate || rateDate;
        setFx({ rate, rateDate: rd, loading: false, error: null });
        if (Number.isFinite(rate) && rate > 0) {
          onConverted?.(Math.round(usd * rate));
          onFxMeta?.({ exchange_rate: rate, exchange_rate_date: rd });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setFx({ rate: null, rateDate: null, loading: false, error: e.response?.data?.message || "Could not fetch USD rate" });
      });
    return () => {
      cancelled = true;
    };
  }, [amountUsd, rateDate]);

  if (!amountUsd || Number(amountUsd) <= 0) return null;
  if (fx.loading) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Fetching USD → INR rate for {rateDate}…
      </Typography>
    );
  }
  if (fx.error) {
    return (
      <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
        {fx.error}
      </Typography>
    );
  }
  if (fx.rate) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        1 USD = {fx.rate.toFixed(2)} INR (as of {fx.rateDate}) → INR filled automatically
      </Typography>
    );
  }
  return null;
}

function SpendStatusChip({ row }) {
  const status = resolveSpendDisplayStatus(row);
  const colors = SPEND_PAYMENT_STATUS_COLORS[status] || SPEND_PAYMENT_STATUS_COLORS.pending;
  const label =
    status === "overdue"
      ? "Overdue"
      : SPEND_PAYMENT_STATUS_LABELS[status] || SPEND_PAYMENT_STATUS_LABELS.pending;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        fontWeight: 700,
        fontSize: "0.7rem",
        height: 22,
        bgcolor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
      }}
    />
  );
}

/** Edit a single logged spend in its own dialog (read-only rows now live in the parent). */
function EditSpendDialog({ open, onClose, projectId, row, onSaved }) {
  const initialMonth = useMemo(() => {
    if (!row?.year_month) return new Date().getMonth();
    const d = dayjs(ymToYmd(row.year_month));
    return d.isValid() ? d.month() : new Date().getMonth();
  }, [row]);
  const initialYear = useMemo(() => {
    if (!row?.year_month) return currentYear();
    const d = dayjs(ymToYmd(row.year_month));
    return d.isValid() ? d.year() : currentYear();
  }, [row]);

  const [label, setLabel] = useState(row?.label ?? "");
  const [amount, setAmount] = useState(row?.amount_inr ?? "");
  const [amountUsd, setAmountUsd] = useState(row?.amount_usd ?? "");
  const [fxMeta, setFxMeta] = useState({
    exchange_rate: row?.exchange_rate ?? null,
    exchange_rate_date: row?.exchange_rate_date ?? null,
  });
  const [monthIndex, setMonthIndex] = useState(initialMonth);
  const [year, setYear] = useState(initialYear);
  const [paymentDue, setPaymentDue] = useState(paymentDueInputValue(row?.payment_due_date));
  const [paymentStatus, setPaymentStatus] = useState(row?.payment_status || "pending");
  const [organization, setOrganization] = useState(row?.organization || BUDGET_ORGANIZATIONS[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(row?.label ?? "");
    setAmount(row?.amount_inr ?? "");
    setAmountUsd(row?.amount_usd ?? "");
    setFxMeta({
      exchange_rate: row?.exchange_rate ?? null,
      exchange_rate_date: row?.exchange_rate_date ?? null,
    });
    setMonthIndex(initialMonth);
    setYear(initialYear);
    setPaymentDue(paymentDueInputValue(row?.payment_due_date));
    setPaymentStatus(row?.payment_status || "pending");
    setOrganization(row?.organization || BUDGET_ORGANIZATIONS[0]);
  }, [open, row, initialMonth, initialYear]);

  const handleSave = async () => {
    const trimmed = String(label || "").trim();
    const amt = Number(amount);
    if (!trimmed) {
      toast.error("Description is required");
      return;
    }
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Amount must be zero or positive");
      return;
    }
    setSaving(true);
    try {
      await updateBudgetLine(projectId, row.id, {
        label: trimmed,
        amount_inr: amt,
        amount_usd: amountUsd ? Number(amountUsd) : "",
        exchange_rate: fxMeta.exchange_rate || "",
        exchange_rate_date: fxMeta.exchange_rate_date || "",
        year_month: ymdFor(year, monthIndex),
        payment_due_date: paymentDue || "",
        payment_status: paymentStatus,
        organization,
      });
      toast.success("Spend updated");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, py: 1.5 }}>Edit spend</DialogTitle>
      <DialogContent dividers sx={{ py: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ p: 1.25, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Currently logged
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.25 }}>
              {row?.label || "—"} · {formatInr(Number(row?.amount_inr ?? 0))} INR ·{" "}
              {row?.year_month ? dayjs(ymToYmd(row.year_month)).format("MMMM YYYY") : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Payment due: {formatDueDisplay(row?.payment_due_date)}
            </Typography>
          </Box>

          <TextField
            label="Description"
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            autoFocus
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              {...outlinedAmountFieldProps}
              label="Amount (INR)"
              type="number"
              inputProps={{ min: 0, step: 1 }}
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setAmountUsd("");
              }}
              sx={{ flex: 1, minWidth: 132 }}
            />
            <TextField
              {...outlinedAmountFieldProps}
              label="Amount (USD)"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={amountUsd}
              onChange={(e) => setAmountUsd(e.target.value)}
              sx={{ flex: 1, minWidth: 132 }}
            />
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Year</InputLabel>
              <Select label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {[0, 1, 2, 3, 4, 5].map((d) => {
                  const y = currentYear() - d;
                  return (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Month</InputLabel>
              <Select
                label="Month"
                value={monthIndex}
                onChange={(e) => setMonthIndex(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    {dayjs(new Date(year, i, 1)).format("MMMM")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <SpendAmountUsdHelper
            amountUsd={amountUsd}
            rateDate={paymentDue || ymdFor(year, monthIndex)}
            onConverted={(inr) => setAmount(String(inr))}
            onFxMeta={(meta) => setFxMeta(meta)}
          />
          <TextField
            label="Payment due"
            size="small"
            type="date"
            value={paymentDue}
            onChange={(e) => setPaymentDue(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Organisation</InputLabel>
            <Select label="Organisation" value={organization} onChange={(e) => setOrganization(e.target.value)}>
              {BUDGET_ORGANIZATIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Payment status</InputLabel>
            <Select
              label="Payment status"
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
            >
              {SPEND_PAYMENT_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {SPEND_PAYMENT_STATUS_LABELS[s]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const ProjectBudgetPreviewDialog = ({
  open,
  onClose,
  projectId,
  projectName,
  projectDepartment,
  yearlyBudget = 0,
  organizationView: organizationViewProp = "",
  canEdit,
  onSaved,
}) => {
  const { user, role } = useContext(AuthContext);
  const [lines, setLines] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [organizationView, setOrganizationView] = useState(
    () => organizationViewProp || getStoredBudgetOrganizationView()
  );
  const [departmentLabel, setDepartmentLabel] = useState(projectDepartment || "");
  const [periodFilter, setPeriodFilter] = useState("last_and_current");
  const [pickMonthIndex, setPickMonthIndex] = useState(() => new Date().getMonth());
  const [loading, setLoading] = useState(false);
  const [newSpend, setNewSpend] = useState({
    label: "",
    amount_inr: "",
    amount_usd: "",
    exchange_rate: null,
    exchange_rate_date: null,
    monthIndex: new Date().getMonth(),
    payment_due_date: "",
    payment_status: "pending",
    organization: organizationViewProp || getStoredBudgetOrganizationView() || BUDGET_ORGANIZATIONS[0],
  });
  const [editingSpend, setEditingSpend] = useState(null);
  /** Holds the row pending delete-confirmation; null when no confirmation is open. */
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  /** Yearly budget editor state. Local string for free typing; saved value on the project. */
  const [savedYearlyBudget, setSavedYearlyBudget] = useState(Number(yearlyBudget) || 0);
  const [yearlyBudgetInput, setYearlyBudgetInput] = useState(String(Number(yearlyBudget) || 0));
  const [savingYearlyBudget, setSavingYearlyBudget] = useState(false);

  const now = useMemo(() => new Date(), []);
  const prevMonthRef = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  }, []);

  const thisMonthY = now.getFullYear();
  const thisMonthM = now.getMonth();

  useEffect(() => {
    setOrganizationView(organizationViewProp || getStoredBudgetOrganizationView());
  }, [open, organizationViewProp]);

  useEffect(() => {
    const onOrgChange = (e) => setOrganizationView(e.detail || "");
    window.addEventListener("budgetOrganizationViewChanged", onOrgChange);
    return () => window.removeEventListener("budgetOrganizationViewChanged", onOrgChange);
  }, []);

  useEffect(() => {
    if (projectDepartment) setDepartmentLabel(projectDepartment);
  }, [projectDepartment]);

  const spendPermissionCtx = useMemo(
    () => ({ userId: user?.id, role, canEdit }),
    [user?.id, role, canEdit]
  );

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const orgFilter = organizationView || undefined;
      const [linesRes, rollRes, projectRes] = await Promise.all([
        getBudgetLines(projectId),
        getBudgetRollup(projectId, year, orgFilter),
        getProjectById(projectId).catch(() => ({ data: null })),
      ]);
      setLines(linesRes.data || []);
      setRollup(rollRes.data || null);
      if (projectRes?.data?.department) setDepartmentLabel(projectRes.data.department);
      const fromDb = Number(projectRes?.data?.budgetallocated);
      if (Number.isFinite(fromDb) && fromDb >= 0) {
        setSavedYearlyBudget(fromDb);
        setYearlyBudgetInput(fromDb > 0 ? String(fromDb) : "");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not load budget data");
      setLines([]);
      setRollup(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, year, organizationView]);

  useEffect(() => {
    if (!open) return;
    const v = Number(yearlyBudget) || 0;
    setSavedYearlyBudget(v);
    setYearlyBudgetInput(v ? String(v) : "");
  }, [open, yearlyBudget, projectId]);

  useEffect(() => {
    if (!open || !projectId) return;
    load();
  }, [open, projectId, year, organizationView, load]);

  useEffect(() => {
    if (!open) return;
    const defaultOrg = organizationView || BUDGET_ORGANIZATIONS[0];
    setNewSpend((s) => ({ ...s, organization: defaultOrg }));
  }, [open, organizationView]);

  useEffect(() => {
    if (year === currentYear()) {
      setNewSpend((s) => ({ ...s, monthIndex: new Date().getMonth() }));
    } else {
      setNewSpend((s) => ({ ...s, monthIndex: 0 }));
    }
  }, [year]);

  useEffect(() => {
    if (periodFilter === "this_month") setYear(thisMonthY);
  }, [periodFilter, thisMonthY]);

  const requestDeleteLine = (row) => setPendingDelete(row);
  const cancelDeleteLine = () => {
    if (deleting) return;
    setPendingDelete(null);
  };
  const confirmDeleteLine = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteBudgetLine(projectId, pendingDelete.id);
      toast.success("Removed");
      setPendingDelete(null);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveYearlyBudget = async () => {
    const trimmed = String(yearlyBudgetInput || "").trim();
    if (trimmed === "") {
      toast.error("Enter a yearly budget (use 0 to clear)");
      return;
    }
    const v = Number(trimmed);
    if (!Number.isFinite(v) || v < 0) {
      toast.error("Yearly budget must be zero or positive");
      return;
    }
    setSavingYearlyBudget(true);
    try {
      await updateProject(projectId, { budgetallocated: v });
      setSavedYearlyBudget(v);
      toast.success(v > 0 ? "Yearly budget updated" : "Yearly budget cleared");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save yearly budget");
    } finally {
      setSavingYearlyBudget(false);
    }
  };

  const loggedSpendYearTotal = useMemo(() => {
    let total = 0;
    for (const row of lines) {
      if (!isSpendRow(row)) continue;
      if (!spendMatchesOrg(row, organizationView)) continue;
      const d = dayjs(ymToYmd(row.year_month));
      if (d.isValid() && d.year() === year) total += Number(row.amount_inr) || 0;
    }
    return total;
  }, [lines, year, organizationView]);

  const displayYearTotal = Math.max(Number(rollup?.yearTotal) || 0, loggedSpendYearTotal);

  const yearlyBudgetSummary = useMemo(() => {
    const budget = Number(savedYearlyBudget) || 0;
    const spent = displayYearTotal;
    if (budget <= 0) return null;
    const pct = budget > 0 ? Math.min(999, Math.max(0, (spent / budget) * 100)) : 0;
    const remaining = Math.max(0, budget - spent);
    const over = Math.max(0, spent - budget);
    let tone = "ok";
    let label = "On track";
    if (over > 0) {
      tone = "over";
      label = "Over budget";
    } else if (pct > ((new Date().getMonth() + 1) / 12) * 100 + 10) {
      tone = "warn";
      label = "Pacing ahead";
    }
    return { budget, spent, pct, remaining, over, tone, label };
  }, [savedYearlyBudget, displayYearTotal]);

  const handleAddSpend = async () => {
    const label = String(newSpend.label || "").trim();
    const amt = Number(newSpend.amount_inr);
    if (!label) {
      toast.error("Label is required");
      return;
    }
    if (Number.isNaN(amt) || amt < 0) {
      toast.error("Amount must be zero or positive");
      return;
    }
    const ym = ymdFor(year, newSpend.monthIndex);
    try {
      await createBudgetLine(projectId, {
        label,
        amount_inr: amt,
        amount_usd: newSpend.amount_usd ? Number(newSpend.amount_usd) : undefined,
        exchange_rate: newSpend.exchange_rate || undefined,
        exchange_rate_date: newSpend.exchange_rate_date || undefined,
        year_month: ym,
        payment_due_date: newSpend.payment_due_date || "",
        payment_status: newSpend.payment_status || "pending",
        organization: newSpend.organization || BUDGET_ORGANIZATIONS[0],
      });
      toast.success("Spend added");
      setNewSpend({
        label: "",
        amount_inr: "",
        amount_usd: "",
        exchange_rate: null,
        exchange_rate_date: null,
        monthIndex: year === currentYear() ? new Date().getMonth() : 0,
        payment_due_date: "",
        payment_status: "pending",
        organization: organizationView || BUDGET_ORGANIZATIONS[0],
      });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not add spend");
    }
  };

  const handleUpdateSpendStatus = async (row, nextStatus) => {
    if (!row?.id || row.payment_status === nextStatus) return;
    if (!canModifySpendRow(row, spendPermissionCtx)) {
      toast.error("You can only update spends you created");
      return;
    }
    try {
      await updateBudgetLinePaymentStatus(projectId, row.id, nextStatus);
      setLines((prev) =>
        prev.map((line) => (line.id === row.id ? { ...line, payment_status: nextStatus } : line))
      );
      toast.success("Status updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not update status");
      await load();
    }
  };

  const spendTable = (y, m0, title) => {
    const rows = spendsInMonth(lines, y, m0, organizationView);
    const sub = sumAmount(rows);
    const showActionsCol = canEdit && rows.some((row) => canModifySpendRow(row, spendPermissionCtx));
    return (
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden", minHeight: 120 }}>
        <Box sx={{ px: 1.25, py: 0.75, bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            <Chip size="small" label={`Total ${formatInr(sub)}`} sx={{ fontWeight: 700 }} />
          </Stack>
        </Box>
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": denseCell }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: "grey.50" }}>What</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 72, bgcolor: "grey.50" }}>Org</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 100, bgcolor: "grey.50" }} align="right">
                  INR
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120, bgcolor: "grey.50" }}>Payment due</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 110, bgcolor: "grey.50" }}>Status</TableCell>
                {showActionsCol ? (
                  <TableCell sx={{ fontWeight: 700, width: 96, bgcolor: "grey.50" }} align="right">
                    Actions
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showActionsCol ? 6 : 5}>
                    <Typography variant="caption" color="text.secondary">
                      {organizationView
                        ? `No ${organizationView} spends logged for this month.`
                        : "No spends logged for this month."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const canModify = canModifySpendRow(row, spendPermissionCtx);
                  return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                        {row.label || "—"}
                      </Typography>
                      {row.created_by_name ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          by {row.created_by_name}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.organization || "—"} sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontSize: "0.8125rem", fontWeight: 700, color: "#0f172a" }}>
                        {formatInr(Number(row.amount_inr) || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                        {formatDueDisplay(row.payment_due_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {canModify ? (
                        <Select
                          size="small"
                          value={row.payment_status || "pending"}
                          onChange={(e) => handleUpdateSpendStatus(row, e.target.value)}
                          sx={{ fontSize: "0.75rem", minWidth: 100, height: 28 }}
                        >
                          {SPEND_PAYMENT_STATUSES.map((s) => (
                            <MenuItem key={s} value={s} sx={{ fontSize: "0.8125rem" }}>
                              {SPEND_PAYMENT_STATUS_LABELS[s]}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <SpendStatusChip row={row} />
                      )}
                    </TableCell>
                    {showActionsCol ? (
                      <TableCell align="right">
                        {canModify ? (
                          <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                            <Tooltip title="Edit spend">
                              <IconButton size="small" color="primary" onClick={() => setEditingSpend(row)} aria-label="Edit">
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => requestDeleteLine(row)} aria-label="Delete">
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const mainPanels = () => {
    if (periodFilter === "last_and_current") {
      const curY = thisMonthY;
      const curM = thisMonthM;
      const prevY = prevMonthRef.y;
      const prevM = prevMonthRef.m;
      return (
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 6 }}>{spendTable(prevY, prevM, monthTitle(prevY, prevM))}</Grid>
          <Grid size={{ xs: 12, md: 6 }}>{spendTable(curY, curM, monthTitle(curY, curM))}</Grid>
        </Grid>
      );
    }
    if (periodFilter === "this_month") {
      return spendTable(thisMonthY, thisMonthM, monthTitle(thisMonthY, thisMonthM));
    }
    if (periodFilter === "pick_month") {
      return (
        <Stack spacing={1}>
          <FormControl size="small" sx={{ maxWidth: 220 }}>
            <InputLabel id="pick-m-label">Month</InputLabel>
            <Select
              labelId="pick-m-label"
              label="Month"
              value={pickMonthIndex}
              onChange={(e) => setPickMonthIndex(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <MenuItem key={i} value={i}>
                  {dayjs(new Date(year, i, 1)).format("MMMM")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {spendTable(year, pickMonthIndex, monthTitle(year, pickMonthIndex))}
        </Stack>
      );
    }
    /* year_overview */
    return (
      <Grid container spacing={0.75} sx={{ maxHeight: 480, overflow: "auto", pr: 0.5 }}>
        {Array.from({ length: 12 }, (_, m) => (
          <Grid key={m} size={{ xs: 12, sm: 6, md: 4 }}>
            {spendTable(year, m, monthTitle(year, m))}
          </Grid>
        ))}
      </Grid>
    );
  };

  const chipsRow = () => {
    if (!rollup) return null;
    const cur = spendsInMonth(lines, thisMonthY, thisMonthM, organizationView);
    const prev = spendsInMonth(lines, prevMonthRef.y, prevMonthRef.m, organizationView);
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1, mb: 1 }}>
        {departmentLabel ? (
          <Chip size="small" label={`Dept: ${departmentLabel}`} sx={{ fontWeight: 700, bgcolor: "#f1f5f9" }} />
        ) : null}
        {organizationView ? (
          <Chip size="small" color="primary" label={`Org: ${organizationView}`} sx={{ fontWeight: 700 }} />
        ) : (
          <Chip size="small" variant="outlined" label="All organisations" sx={{ fontWeight: 700 }} />
        )}
        <Chip
          size="small"
          variant="outlined"
          label={`${monthTitle(prevMonthRef.y, prevMonthRef.m)} spends: ${formatInr(sumAmount(prev))}`}
        />
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={`${monthTitle(thisMonthY, thisMonthM)} spends: ${formatInr(sumAmount(cur))}`}
        />
        <Chip size="small" variant="outlined" label={`${year} total: ${formatInr(displayYearTotal)}`} />
      </Stack>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              width: { xs: "100%", sm: "calc(100% - 24px)" },
              height: { xs: "100%", sm: "calc(100% - 24px)" },
              maxHeight: { xs: "100%", sm: "calc(100% - 24px)" },
              m: { xs: 0, sm: 1.5 },
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            py: 1.25,
            pl: 2,
            pr: 1,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <span>Budget preview{projectName ? ` — ${projectName}` : ""}</span>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            px: 2,
            py: 1.25,
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1, mb: 1, flexShrink: 0 }} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="budget-year-label">Year</InputLabel>
              <Select labelId="budget-year-label" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {[0, 1, 2, 3, 4, 5].map((d) => {
                  const y = currentYear() - d;
                  return (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="period-label">Show</InputLabel>
              <Select labelId="period-label" label="Show" value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
                {PERIOD_FILTERS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="modal-org-label">Organisation</InputLabel>
              <Select
                labelId="modal-org-label"
                label="Organisation"
                value={organizationView || ""}
                onChange={(e) => setOrganizationView(e.target.value)}
              >
                <MenuItem value="">All organisations</MenuItem>
                {BUDGET_ORGANIZATIONS.map((o) => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {departmentLabel ? (
              <Chip size="small" label={`Dept: ${departmentLabel}`} sx={{ fontWeight: 700, alignSelf: "center" }} />
            ) : null}
          </Stack>

          {chipsRow()}

          <Box
            sx={{
              mb: 1.25,
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              flexShrink: 0,
              bgcolor: "#f8fafc",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.25}
              alignItems={{ md: "center" }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", minWidth: 110 }}>
                Yearly budget
              </Typography>
              {canEdit ? (
                <>
                  <TextField
                    size="small"
                    label="Yearly budget (INR)"
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={yearlyBudgetInput}
                    onChange={(e) => setYearlyBudgetInput(e.target.value)}
                    sx={{ width: { xs: "100%", md: 200 } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveYearlyBudget}
                    disabled={savingYearlyBudget || Number(yearlyBudgetInput) === Number(savedYearlyBudget)}
                    sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                  >
                    {savingYearlyBudget ? "Saving…" : "Save"}
                  </Button>
                </>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                  {savedYearlyBudget > 0 ? `${formatInr(savedYearlyBudget)} INR` : "Not set"}
                </Typography>
              )}

              {yearlyBudgetSummary ? (
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ ml: { md: "auto" } }}>
                  <Chip
                    size="small"
                    label={`Spent ${formatInr(yearlyBudgetSummary.spent)} of ${formatInr(yearlyBudgetSummary.budget)} (${yearlyBudgetSummary.pct.toFixed(1)}%)`}
                    sx={{ fontWeight: 700 }}
                  />
                  {yearlyBudgetSummary.over > 0 ? (
                    <Chip
                      size="small"
                      color="error"
                      label={`Over by ${formatInr(yearlyBudgetSummary.over)}`}
                      sx={{ fontWeight: 700 }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      label={`Remaining ${formatInr(yearlyBudgetSummary.remaining)}`}
                      sx={{ fontWeight: 700, bgcolor: "#ecfdf5", color: "#047857" }}
                    />
                  )}
                  <Chip
                    size="small"
                    label={yearlyBudgetSummary.label}
                    sx={{
                      fontWeight: 700,
                      bgcolor:
                        yearlyBudgetSummary.tone === "over"
                          ? "#fee2e2"
                          : yearlyBudgetSummary.tone === "warn"
                            ? "#fef3c7"
                            : "#e0f2fe",
                      color:
                        yearlyBudgetSummary.tone === "over"
                          ? "#b91c1c"
                          : yearlyBudgetSummary.tone === "warn"
                            ? "#92400e"
                            : "#0369a1",
                    }}
                  />
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ ml: { md: "auto" } }}>
                  Set a yearly budget to track YTD spend vs target on the dashboard.
                </Typography>
              )}
            </Stack>
          </Box>

          {canEdit ? (
            <Box
              sx={{
                mb: 1.5,
                p: 1.25,
                border: "1px dashed",
                borderColor: "primary.light",
                borderRadius: 1,
                flexShrink: 0,
                bgcolor: "rgba(18, 114, 179, 0.06)",
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800, display: "block", mb: 0.75 }}>
                Add spend for {monthTitle(year, newSpend.monthIndex)}
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  label="Description"
                  value={newSpend.label}
                  onChange={(e) => setNewSpend((s) => ({ ...s, label: e.target.value }))}
                  sx={{ flex: "1 1 200px", minWidth: 160 }}
                />
                <TextField
                  {...outlinedAmountFieldProps}
                  label="Amount (INR)"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={newSpend.amount_inr}
                  onChange={(e) => setNewSpend((s) => ({ ...s, amount_inr: e.target.value, amount_usd: "" }))}
                  sx={{ flex: "0 1 140px", minWidth: 132, width: { xs: "100%", md: 140 } }}
                />
                <TextField
                  {...outlinedAmountFieldProps}
                  label="Amount (USD)"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={newSpend.amount_usd}
                  onChange={(e) => setNewSpend((s) => ({ ...s, amount_usd: e.target.value }))}
                  sx={{ flex: "0 1 140px", minWidth: 132, width: { xs: "100%", md: 140 } }}
                />
                <FormControl size="small" sx={{ flex: "0 1 150px", minWidth: 140 }}>
                  <InputLabel id="ns-month">Month</InputLabel>
                  <Select
                    labelId="ns-month"
                    label="Month"
                    value={newSpend.monthIndex}
                    onChange={(e) => setNewSpend((s) => ({ ...s, monthIndex: Number(e.target.value) }))}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i} value={i}>
                        {dayjs(new Date(year, i, 1)).format("MMMM")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: "0 1 120px", minWidth: 110 }}>
                  <InputLabel id="ns-org">Organisation</InputLabel>
                  <Select
                    labelId="ns-org"
                    label="Organisation"
                    value={newSpend.organization}
                    onChange={(e) => setNewSpend((s) => ({ ...s, organization: e.target.value }))}
                  >
                    {BUDGET_ORGANIZATIONS.map((o) => (
                      <MenuItem key={o} value={o}>{o}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Payment due"
                  type="date"
                  value={newSpend.payment_due_date}
                  onChange={(e) => setNewSpend((s) => ({ ...s, payment_due_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: "0 1 160px", width: { xs: "100%", md: 160 } }}
                />
                <FormControl size="small" sx={{ flex: "0 1 130px", minWidth: 120 }}>
                  <InputLabel id="ns-status">Status</InputLabel>
                  <Select
                    labelId="ns-status"
                    label="Status"
                    value={newSpend.payment_status}
                    onChange={(e) => setNewSpend((s) => ({ ...s, payment_status: e.target.value }))}
                  >
                    {SPEND_PAYMENT_STATUSES.map((s) => (
                      <MenuItem key={s} value={s}>
                        {SPEND_PAYMENT_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddSpend}
                  sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                >
                  Add spend
                </Button>
              </Stack>
              <SpendAmountUsdHelper
                amountUsd={newSpend.amount_usd}
                rateDate={newSpend.payment_due_date || ymdFor(year, newSpend.monthIndex)}
                onConverted={(inr) => setNewSpend((s) => ({ ...s, amount_inr: String(inr) }))}
                onFxMeta={({ exchange_rate, exchange_rate_date }) =>
                  setNewSpend((s) => ({ ...s, exchange_rate, exchange_rate_date }))
                }
              />
            </Box>
          ) : null}

          {loading ? (
            <Typography variant="body2">Loading…</Typography>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
              {mainPanels()}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingDelete}
        onClose={cancelDeleteLine}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800, py: 1.5, pr: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Remove this line?</span>
          <IconButton
            onClick={cancelDeleteLine}
            aria-label="Close"
            size="small"
            disabled={deleting}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            This will permanently delete{" "}
            <Box component="strong" sx={{ fontWeight: 700 }}>
              {pendingDelete?.label || "this line"}
            </Box>
            {pendingDelete ? (
              <>
                {" "}({formatInr(Number(pendingDelete?.amount_inr) || 0)} INR
                {pendingDelete?.year_month
                  ? `, ${dayjs(ymToYmd(pendingDelete.year_month)).format("MMMM YYYY")}`
                  : ""}
                )
              </>
            ) : null}
            . This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={cancelDeleteLine} disabled={deleting} sx={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteLine}
            disabled={deleting}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deleting ? "Removing…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <EditSpendDialog
        open={!!editingSpend}
        onClose={() => setEditingSpend(null)}
        projectId={projectId}
        row={editingSpend}
        onSaved={async () => {
          await load();
          onSaved?.();
        }}
      />
    </>
  );
};

export default ProjectBudgetPreviewDialog;
